// src/features/system/developer/components/video-gallery/ShareLinkButton.tsx
// ────────────────────────────────────────────────────────────────────────────
// Reusable transparent PAC-mirror share button.
//
// Single source of truth for "mint a public shared-link, copy to clipboard"
// across the Videos surface (SOLID: SRP — one component owns the share
// affordance; OCP — adding a new scope variant only flips a prop).
//
// Two modes via a single optional `videoId` prop:
//   • absent              → gallery share (entire tenant feed, scope_key="gallery")
//   • non-empty string    → per-video share (scope_key="video:<videoId>")
//
// The shared-link consumer (`/s/:token` route → SharedScopeProvider →
// VideoGalleryViewer) reads `meta.scope_key`, recognises the `video:` prefix,
// forwards it as `pinnedVideoId` to VideoGalleryPanel, and the BFF
// `/shared/:token/video-pipeline/*` guards enforce the same convention on
// the server side (defence in depth — never trust the FE alone).
// ────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import { createSharedLink } from '@/features/shared-scope/api/sharedScopeApi';

export interface ShareLinkButtonProps {
  /**
   * When set, the minted link pins the public viewer to this single
   * video (scope_key="video:<videoId>"). When absent the link shares
   * the whole gallery (scope_key="gallery") — the historical default.
   */
  videoId?: string;
  /**
   * Optional human label baked into the link (surfaced in the admin
   * panel). Falls back to a stable, defensive default.
   */
  label?:   string;
  /**
   * Size variant. `'sm'` matches the right-rail action buttons in
   * VideoFeed slides; `'md'` matches the header overlay (current
   * gallery-wide share button).
   */
  size?:    'sm' | 'md';
  /**
   * Visual emphasis variant. `'header'` (default) is the round glass
   * disc used in the gallery header. `'rail'` matches the
   * 44 × 44 TikTok-rail action pills used inside a slide.
   */
  variant?: 'header' | 'rail';
  /**
   * Optional className passthrough for layout (e.g. positioning inside
   * a hover-revealed card overlay).
   */
  className?: string;
}

/**
 * Shared-link scope key convention. Centralised so both producer (this
 * button) and consumer (VideoGalleryViewer, BFF guard) agree on the
 * single string contract — no magic literals scattered through the
 * codebase.
 */
export const VIDEO_SCOPE_KEY_PREFIX = 'video:' as const;
export const GALLERY_SCOPE_KEY      = 'gallery' as const;
export const VIDEO_SCOPE_NS         = 'media.video' as const;
export const VIDEO_SCOPE_TYPE       = 'video-gallery' as const;

export function buildVideoScopeKey(videoId: string): string {
  return `${VIDEO_SCOPE_KEY_PREFIX}${videoId}`;
}

export function parsePinnedVideoIdFromScopeKey(scopeKey: string | undefined | null): string | null {
  if (!scopeKey || !scopeKey.startsWith(VIDEO_SCOPE_KEY_PREFIX)) return null;
  const raw = scopeKey.slice(VIDEO_SCOPE_KEY_PREFIX.length).trim();
  let id = raw;
  try {
    id = decodeURIComponent(raw);
  } catch {
    id = raw;
  }
  return id.length > 0 ? id : null;
}

export default function ShareLinkButton({
  videoId,
  label,
  size = 'md',
  variant = 'header',
  className,
}: ShareLinkButtonProps) {
  const [busy,  setBusy]  = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (toast == null) return;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  const isPerVideo = typeof videoId === 'string' && videoId.length > 0;

  const onClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const scope_key  = isPerVideo ? buildVideoScopeKey(videoId!) : GALLERY_SCOPE_KEY;
      const linkLabel  = label ?? (isPerVideo ? `Video ${videoId}` : 'Video Gallery');
      const record = await createSharedLink({
        scope_type:   VIDEO_SCOPE_TYPE,
        scope_ns:     VIDEO_SCOPE_NS,
        scope_key,
        permissions:  ['read'],
        access_level: 'public',
        label:        linkLabel,
        ttl_hours:    24 * 30,
      });
      const url = (record as { url?: string }).url;
      if (url) {
        try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
        setToast(isPerVideo ? 'Link to this video copied' : 'Public link copied');
      } else {
        setToast('Link created');
      }
    } catch (e) {
      const msg = (e as Error).message || '';
      const isTimeout = /timed out|timeout|aborted/i.test(msg)
        || (e as Error).name === 'TimeoutError';
      setToast(isTimeout
        ? 'Share is taking longer than usual — please try again'
        : `Share failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  }, [busy, isPerVideo, videoId, label]);

  // ── Visual variants ─────────────────────────────────────────────────
  // Both variants render the same SVG glyph (the canonical "share"
  // node-graph) so users learn ONE icon across the surface.
  const railClasses = [
    'w-11 h-11 rounded-full',
    'bg-black/30 hover:bg-black/45',
    'border border-white/15 hover:border-neon_orange-300/60',
    'text-white/90 hover:text-neon_orange-200 backdrop-blur-md',
    'transition-colors duration-150 disabled:opacity-60',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40',
  ].join(' ');

  const headerClasses = [
    'shrink-0 inline-flex items-center justify-center',
    'w-8 h-8 rounded-full',
    'bg-black/50 hover:bg-black/70',
    'border border-white/15 hover:border-neon_orange-300/60',
    'text-white/85 hover:text-neon_orange-200 backdrop-blur-md',
    'transition-colors duration-150 disabled:opacity-60',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40',
  ].join(' ');

  const baseClasses = variant === 'rail' ? railClasses : headerClasses;
  const iconClasses = size === 'sm' || variant === 'rail'
    ? 'w-5 h-5 fill-current'
    : 'w-4 h-4 fill-current';

  const title = isPerVideo
    ? 'Share public link to this video'
    : 'Share public link to this gallery';
  const aria  = isPerVideo ? 'Share this video' : 'Share public link';

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); void onClick(); }}
        disabled={busy}
        title={title}
        aria-label={aria}
        className={[baseClasses, 'inline-flex items-center justify-center', className ?? ''].join(' ')}
      >
        <svg viewBox="0 0 24 24" className={iconClasses} aria-hidden>
          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
        </svg>
      </button>
      {toast != null && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] px-3 py-1.5 rounded-md text-[12px] bg-black/85 text-white border border-white/15 backdrop-blur-md">
          {toast}
        </div>
      )}
    </>
  );
}
